import React, { useState, useEffect } from 'react';
import {
  Edit,
  Trash2,
  Plus,
  Eye,
  EyeOff,
  Move,
  Save,
  X,
  Upload,
  Image as ImageIcon,
  Crown,
  Sparkles,
  Globe,
  Link as LinkIcon,
  Settings,
  Palette
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import ProductSelector from './ProductSelector';

const DemoPhotoManager = () => {
  const { token, user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'hero_slide',
    image_url: '',
    title_fr: '',
    title_en: '',
    subtitle_fr: '',
    subtitle_en: '',
    button_text_fr: '',
    button_text_en: '',
    button_link: '',
    sort_order: 0,
    is_active: true
  });

  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const photoTypes = [
    { value: 'hero_background', label: 'Arrière-plan Hero', icon: '🎨' },
    { value: 'hero_inset', label: 'Insertion Hero', icon: '✨' },
    { value: 'hero_slide', label: 'Slide Hero', icon: '🖼️' }
  ];

  // Fetch all demo photos
  const fetchPhotos = async () => {
    try {
      const response = await fetch('/api/demo-photos/admin/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPhotos(data.photos);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Erreur lors du chargement des photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle product selection for button link
  const handleProductSelect = (link) => {
    setFormData(prev => ({
      ...prev,
      button_link: link
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'hero_slide',
      image_url: '',
      title_fr: '',
      title_en: '',
      subtitle_fr: '',
      subtitle_en: '',
      button_text_fr: '',
      button_text_en: '',
      button_link: '',
      sort_order: 0,
      is_active: true
    });
    setShowForm(false);
    setEditingPhoto(null);
  };

  // Create new photo
  const handleCreate = async (e) => {
    e.preventDefault();

    try {
      let updatedFormData = { ...formData };

      // First, upload any selected files
      if (selectedFile) {
        console.log('Photo file selected, uploading...');
        const photoResult = await uploadFile(selectedFile, 'photo');
        if (photoResult && photoResult.image_url) {
          updatedFormData.image_url = photoResult.image_url;
        }
      }


      console.log('Final formData for creation:', updatedFormData);

      // Now create the photo with the updated formData (which includes the uploaded URLs)
      const response = await fetch('/api/demo-photos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedFormData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Photo créée avec succès ! ✨');
        resetForm();
        fetchPhotos();
      } else {
        toast.error(data.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating photo:', error);
      toast.error('Erreur lors de la création');
    }
  };

  // Update photo
  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      let updatedFormData = { ...formData };

      // Only upload files if new ones were selected
      if (selectedFile) {
        console.log('New photo file selected, uploading...');
        const photoResult = await uploadFile(selectedFile, 'photo');
        if (photoResult && photoResult.image_url) {
          updatedFormData.image_url = photoResult.image_url;
        }
      } else {
        console.log('No new photo file selected, keeping existing:', editingPhoto.image_url);
        // Ensure we keep the existing image_url if no new file is selected
        updatedFormData.image_url = editingPhoto.image_url || formData.image_url;
      }



      console.log('Final formData for update:', updatedFormData);

      // Now update the photo with the updated formData (which includes the uploaded URLs)
      const response = await fetch(`/api/demo-photos/${editingPhoto.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedFormData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Photo mise à jour avec succès ! ✨');
        resetForm();
        fetchPhotos();
      } else {
        toast.error(data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating photo:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete photo
  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ? Cette action est irréversible.')) return;

    try {
      const response = await fetch(`/api/demo-photos/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Photo supprimée avec succès ! 🗑️');
        fetchPhotos();
      } else {
        toast.error(data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Edit photo
  const handleEdit = (photo) => {
    setEditingPhoto(photo);
    setFormData({
      name: photo.name,
      type: photo.type,
      image_url: photo.image_url,

      title_fr: photo.title_fr || '',
      title_en: photo.title_en || '',
      subtitle_fr: photo.subtitle_fr || '',
      subtitle_en: photo.subtitle_en || '',
      button_text_fr: photo.button_text_fr || '',
      button_text_en: photo.button_text_en || '',
      button_link: photo.button_link || '',
      sort_order: photo.sort_order || 0,
      is_active: photo.is_active
    });
    // Clear any previously selected files when editing
    setSelectedFile(null);
    setShowForm(true);
  };

  // File upload function
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFormData(prev => ({ ...prev, image_url: '' })); // Clear URL if file is selected
    }
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch('/api/demo-photos/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        // Update the form state
        setFormData(prev => ({ ...prev, image_url: data.file.url }));
        setSelectedFile(null);
        toast.success('Photo téléchargée avec succès ! 📸');

        // Return the uploaded file information
        return {
          image_url: data.file.url
        };
      } else {
        toast.error(data.message || 'Erreur lors du téléchargement');
        return null;
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Erreur lors du téléchargement');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Toggle photo status
  const toggleStatus = async (id, currentStatus) => {
    try {
      const photo = photos.find(p => p.id === id);
      const response = await fetch(`/api/demo-photos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...photo, is_active: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Statut mis à jour avec succès ! 🔄');
        fetchPhotos();
      } else {
        toast.error(data.message || 'Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Chargement des photos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-yellow-500 p-3 rounded-full">
              <Crown className="h-8 w-8 text-gray-900" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">TOLOU Admin</h1>
              <p className="text-yellow-400 text-lg">Gestionnaire de Photos Démo</p>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-gray-300">
                <ImageIcon className="h-5 w-5" />
                <span>{photos.length} photos configurées</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300">
                <Sparkles className="h-5 w-5" />
                <span>{photos.filter(p => p.is_active).length} actives</span>
              </div>
            </div>

            <button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-gray-900 font-bold px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Nouvelle Photo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-8 py-6 rounded-t-2xl border-b border-gray-600">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="bg-yellow-500 p-2 rounded-lg">
                      <Palette className="h-6 w-6 text-gray-900" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {editingPhoto ? 'Modifier la Photo' : 'Nouvelle Photo Démo'}
                    </h3>
                  </div>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-8">
                <form onSubmit={editingPhoto ? handleUpdate : handleCreate} className="space-y-8">
                  {/* Basic Information */}
                  <div className="bg-gray-700/50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center space-x-2">
                      <Settings className="h-5 w-5" />
                      <span>Informations de Base</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Nom de la Photo
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                          placeholder="Nom unique pour identifier cette photo"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Type de Photo
                        </label>
                        <select
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white"
                        >
                          {photoTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Image Upload Section */}
                  <div className="bg-gray-700/50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center space-x-2">
                      <ImageIcon className="h-5 w-5" />
                      <span>Images</span>
                    </h4>

                    <div className="grid grid-cols-1 gap-8">
                      {/* Main Image */}
                      <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Image Principale
                        </label>

                        {/* File Upload */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileSelect(e, 'photo')}
                              className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-yellow-500 file:text-gray-900 hover:file:bg-yellow-600"
                            />
                            {selectedFile && (
                              <div className="text-sm text-green-400 font-medium flex items-center space-x-1">
                                <span>✓</span>
                                <span>Nouveau fichier sélectionné</span>
                              </div>
                            )}
                          </div>

                          {/* Current Photo Info */}
                          {editingPhoto && editingPhoto.image_url && !selectedFile && (
                            <div className="text-sm text-blue-400 bg-blue-900/20 p-3 rounded-lg border border-blue-800">
                              📷 Photo actuelle : {editingPhoto.image_url.split('/').pop()}
                            </div>
                          )}
                        </div>

                        {/* URL Input */}
                        <div className="space-y-2">
                          <div className="text-sm text-gray-400">Ou entrer une URL :</div>
                          <input
                            type="text"
                            name="image_url"
                            value={formData.image_url}
                            onChange={handleInputChange}
                            placeholder="https://example.com/image.jpg ou /uploads/photo.jpg"
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                          />
                        </div>

                        {/* Preview */}
                        {(formData.image_url || selectedFile) && (
                          <div className="mt-4">
                            <div className="text-sm text-gray-400 mb-2">Aperçu :</div>
                            <div className="w-40 h-40 border-2 border-gray-600 rounded-xl overflow-hidden bg-gray-800">
                              {selectedFile ? (
                                <img
                                  src={URL.createObjectURL(selectedFile)}
                                  alt="Aperçu"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <img
                                  src={formData.image_url}
                                  alt="Aperçu"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/160x160/374151/9CA3AF?text=Aucune+Image';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>


                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="bg-gray-700/50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center space-x-2">
                      <Globe className="h-5 w-5" />
                      <span>Contenu Multilingue</span>
                    </h4>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* French Content */}
                      <div className="space-y-4">
                        <h5 className="text-md font-medium text-blue-400 flex items-center space-x-2">
                          🇫🇷 Français
                        </h5>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Titre
                            </label>
                            <input
                              type="text"
                              name="title_fr"
                              value={formData.title_fr}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                              placeholder="Titre en français"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Sous-titre
                            </label>
                            <input
                              type="text"
                              name="subtitle_fr"
                              value={formData.subtitle_fr}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                              placeholder="Sous-titre en français"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Texte du Bouton
                            </label>
                            <input
                              type="text"
                              name="button_text_fr"
                              value={formData.button_text_fr}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                              placeholder="Ex: Voir plus, Découvrir, etc."
                            />
                          </div>
                        </div>
                      </div>

                      {/* English Content */}
                      <div className="space-y-4">
                        <h5 className="text-md font-medium text-green-400 flex items-center space-x-2">
                          🇬🇧 English
                        </h5>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Title
                            </label>
                            <input
                              type="text"
                              name="title_en"
                              value={formData.title_en}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                              placeholder="Title in English"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Subtitle
                            </label>
                            <input
                              type="text"
                              name="subtitle_en"
                              value={formData.subtitle_en}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                              placeholder="Subtitle in English"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Button Text
                            </label>
                            <input
                              type="text"
                              name="button_text_en"
                              value={formData.button_text_en}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                              placeholder="Ex: See more, Discover, etc."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Button Configuration */}
                  <div className="bg-gray-700/50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center space-x-2">
                      <LinkIcon className="h-5 w-5" />
                      <span>Configuration du Bouton</span>
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Product Selector */}
                      <div>
                        <ProductSelector
                          onProductSelect={handleProductSelect}
                          currentLink={formData.button_link}
                        />
                      </div>

                      {/* Manual Link Input (Fallback) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Lien Manuel (optionnel)
                        </label>
                        <input
                          type="text"
                          name="button_link"
                          value={formData.button_link}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                          placeholder="/about, /categories, https://example.com"
                        />
                        <p className="text-sm text-gray-400 mt-2">
                          URL personnalisée (ex: /about, /categories) ou externe
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Ordre de Tri
                        </label>
                        <input
                          type="number"
                          name="sort_order"
                          value={formData.sort_order}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white"
                          placeholder="0"
                        />
                        <p className="text-sm text-gray-400 mt-2">
                          Plus le nombre est petit, plus la photo apparaîtra en premier
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="bg-gray-700/50 rounded-xl p-6">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="w-5 h-5 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500 bg-gray-700"
                      />
                      <label className="text-lg font-medium text-gray-300">
                        Photo Active
                      </label>
                      <span className="text-sm text-gray-400">
                        (Visible sur le site public)
                      </span>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-4 pt-6">
                    <button
                      type="submit"
                      disabled={uploading}
                      className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:from-gray-600 disabled:to-gray-700 text-gray-900 font-bold px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2 disabled:transform-none disabled:cursor-not-allowed"
                    >
                      <Save className="h-5 w-5" />
                      <span>
                        {uploading ? 'Téléchargement...' : (editingPhoto ? 'Mettre à Jour' : 'Créer la Photo')}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-8 py-4 rounded-xl transition-colors duration-300"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Photos List */}
        <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-8 py-6 border-b border-gray-600">
            <h3 className="text-2xl font-bold text-white flex items-center space-x-3">
              <ImageIcon className="h-7 w-7 text-yellow-400" />
              <span>Photos Configurées</span>
              <span className="text-yellow-400 text-lg">({photos.length})</span>
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-8 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Photo
                  </th>
                  <th className="px-8 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Détails
                  </th>
                  <th className="px-8 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-8 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-8 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {photos.map((photo) => (
                  <tr key={photo.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex-shrink-0 h-20 w-20">
                        <img
                          className="h-20 w-20 rounded-xl object-cover border-2 border-gray-600 hover:border-yellow-500 transition-colors"
                          src={photo.image_url}
                          alt={photo.name}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/80x80/374151/9CA3AF?text=No+Image';
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="space-y-2">
                        <div className="text-lg font-semibold text-white">{photo.name}</div>
                        <div className="text-sm text-gray-300">{photo.title_fr || 'Aucun titre'}</div>
                        <div className="text-sm text-gray-400">Ordre: {photo.sort_order}</div>
                        {photo.button_link && (
                          <div className="text-sm text-blue-400 flex items-center space-x-1">
                            <LinkIcon className="h-3 w-3" />
                            <span className="truncate max-w-32">{photo.button_link}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-2 text-xs font-semibold rounded-full ${
                        photo.type === 'hero_background' ? 'bg-purple-900/30 text-purple-300 border border-purple-600' :
                        photo.type === 'hero_inset' ? 'bg-blue-900/30 text-blue-300 border border-blue-600' :
                        'bg-green-900/30 text-green-300 border border-green-600'
                      }`}>
                        {photoTypes.find(t => t.value === photo.type)?.icon} {photo.type.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <button
                        onClick={() => toggleStatus(photo.id, photo.is_active)}
                        className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
                          photo.is_active
                            ? 'bg-green-900/30 text-green-300 border border-green-600 hover:bg-green-900/50'
                            : 'bg-red-900/30 text-red-300 border border-red-600 hover:bg-red-900/50'
                        }`}
                      >
                        {photo.is_active ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                        <span>
                          {photo.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </button>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEdit(photo)}
                          className="text-blue-400 hover:text-blue-300 p-2 rounded-lg hover:bg-blue-900/30 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(photo.id)}
                          className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-900/30 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {photos.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-4">Aucune photo configurée</div>
              <p className="text-gray-500">Commencez par créer votre première photo démo !</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoPhotoManager;
